import { createApi } from '@reduxjs/toolkit/query/react';

import {
  LeaveRequestFormPostRequest,
  HrmRequestFormResponse,
  ValidationSetting,
} from '@Types/api/form';
import {
  RequestListSearchRequest,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { ApiBaseName, ApiReducerPath, RequestTypeCodes } from '@Enum/index';
import { PAGE_SIZE, TABLE_PAGE_DEFAULT } from '@Features/constants';
import { Balance } from '@Types/api/form/leaveRequest/Balance';
import { customBaseQuery } from '@Utils/api/customBaseQuery';

const REQ_TYPE_FILTER = {
  name: 'requestTypeID',
  value: RequestTypeCodes.LEAVE_REQUEST,
  operator: 'eq',
};

export const leaveRequestApi = createApi({
  tagTypes: ['leaveRequest'],
  reducerPath: ApiReducerPath.API_LEAVE_REQUEST,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getLeaveRequests: builder.mutation<
      RequestListSearchResponse<HrmRequestFormResponse>,
      RequestListSearchRequest
    >({
      query: ({
        filters = [],
        page = TABLE_PAGE_DEFAULT,
        size = PAGE_SIZE,
        sortBy = 'requestId',
        sortOrder = 'desc',
        searchValue,
      }) => ({
        url: `${ApiBaseName.ESS}/hrm-request/search`,
        method: 'post',
        body: {
          searchValue,
          searchCriteriaList: [
            {
              searchCriterias: [...filters, REQ_TYPE_FILTER],
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),
    postLeaveForm: builder.mutation<
      HrmRequestFormResponse,
      LeaveRequestFormPostRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/hrm-request`,
        method: 'post',
        body: {
          ...body,
          requestTypeID: RequestTypeCodes.LEAVE_REQUEST,
        },
      }),
    }),
    formValidation: builder.mutation<
      HrmRequestFormResponse,
      LeaveRequestFormPostRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/hrm-request/validate`,
        method: 'post',
        body,
      }),
    }),
    getValidationSettings: builder.query<Array<ValidationSetting>, void>({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/hrm-request/validation_settings`,
        method: 'get',
        body,
      }),
    }),
    getBalance: builder.mutation<Balance, { empNumber: string; date?: string }>(
      {
        query: ({ empNumber, date }) => ({
          url: `${ApiBaseName.ESS}/hrm-request/available-balance`,
          method: 'get',
          params: {
            empNumber,
            date,
          },
        }),
      }
    ),
  }),
});

export const {
  usePostLeaveFormMutation,
  useFormValidationMutation,
  useGetLeaveRequestsMutation,
  useGetBalanceMutation,
  useGetValidationSettingsQuery,
} = leaveRequestApi;
